/**
 * The Timeline is the view model of a Session: an ordered list of entries a
 * human reads. Durable Agent Run boundaries separate responses even when no
 * new user message arrives. Older logs without boundaries continue grouping
 * agent activity between user messages.
 *
 * Two sources feed it: `fromEvents` rebuilds it from durable Session events,
 * and `applyStream` advances it with live Stream Events during a run. Once a
 * run ends the UI reloads, so durable events always have the final word.
 */
import type { CancelCause, ErrorInfo, ImageAttachment, SessionEvent, SlashResult, StreamEvent } from './types.js'
import { needsAttention } from './tools.js'

export interface ToolView {
  callId: string
  name: string
  args: unknown
  status: 'running' | 'ok' | 'error'
  output?: unknown
  images?: ImageAttachment[]
  error?: string
  /** `Error.name` of a failure, used to tell model-facing errors from ones a person must act on. */
  errorName?: string
  durationMs?: number
  children?: ToolView[]
}

export interface SubagentView {
  callId?: string
  id?: string
  label: string
  task: string
  mode: 'spawn' | 'fork'
  status: 'starting' | 'running' | 'ready' | 'failed'
  replies: string[]
  error?: string
}

export interface EditedFile {
  path: string
  additions?: number
  deletions?: number
}

export type Block =
  | { kind: 'text'; id: string; text: string; streaming?: boolean }
  /** The model's visible reasoning; `startedAt` times a live block until its answer begins. */
  | { kind: 'reasoning'; id: string; text: string; streaming?: boolean; startedAt?: number; durationMs?: number }
  | { kind: 'tool'; id: string; tool: ToolView }
  | { kind: 'subagent'; id: string; agent: SubagentView }
  | { kind: 'files'; id: string; files: EditedFile[] }
  | { kind: 'notice'; id: string; tone: 'info' | 'warn' | 'error'; text: string; process?: boolean }

export type Entry =
  | { kind: 'user'; id: string; text: string; images?: ImageAttachment[]; local?: boolean }
  | {
      kind: 'agent'
      id: string
      blocks: Block[]
      status: 'running' | 'done' | 'stopped' | 'error'
      /** Last durable assistant message id, usable as a fork point. */
      forkId?: string
      turn?: number
      /** Final answer from a successfully closed durable Agent Run. */
      summary?: { text: string; sourceMessageId: string }
    }
  | { kind: 'compaction'; id: string; summary: string; tokensBefore?: number }
  | { kind: 'slash'; id: string; command: string; args: string[]; result: SlashResult }

type AgentEntry = Extract<Entry, { kind: 'agent' }>

const AGENT_NAME = /^agent:([0-9a-f-]{36})$/i
const TERMINAL_REPLY = /^Subagent ([0-9a-f-]{36}) (completed|ended)\b(?: \([^)]*\))?:? ?/i
const STARTED = /^Started subagent ([0-9a-f-]{36})\b/i
const SPAWN_TOOL = 'spawn_subagent'
/** Author of the harness's "finish the task" nudge (`CONTINUATION_NUDGE_NAME` in @tnega/agent). */
const CONTINUATION_NAME = 'plugin:continue'
/** Author of the input that continues a run the app stopped (`RECOVERY_NUDGE_NAME` in @tnega/agent). */
const RECOVERY_NAME = 'plugin:recover'
/** Authors of the loop guard's messages (`LOOP_GUARD_*_NAME` in @tnega/loop-guard; its tests check these stay in sync), and what each one tells the person. */
const LOOP_GUARD_NOTICES: Readonly<Record<string, { text: string; tone: 'info' | 'warn'; process?: boolean }>> = {
  'plugin:loop-guard': { text: 'Noticed the agent repeating itself and nudged it', tone: 'info', process: true },
  'plugin:loop-guard:reflect': { text: 'Asked the agent to rethink its approach', tone: 'info', process: true },
  'plugin:loop-guard:park': { text: 'Paused the agent: it was stuck, so it reports instead', tone: 'warn' },
  'plugin:loop-guard:attempts': { text: 'Reminded the agent of approaches that already failed', tone: 'info', process: true },
}

// ---------------------------------------------------------------------------
// Durable events → Timeline
// ---------------------------------------------------------------------------

export function fromEvents(events: readonly SessionEvent[]): Entry[] {
  const entries: Entry[] = []
  let turn: number | undefined
  const closed = new Set<number>()
  const completed = new Set<number>()
  const finalAnswers = new Map<number, { text: string; sourceMessageId: string }>()
  const byTurn = new Map<number, AgentEntry>()
  const agent = (id: string): AgentEntry => {
    const last = entries.at(-1)
    if (last?.kind === 'agent' && last.turn === turn) return last
    const created: AgentEntry = {
      kind: 'agent', id: `agent-${id}`, blocks: [], status: 'done',
      ...(turn !== undefined ? { turn } : {}),
    }
    entries.push(created)
    if (turn !== undefined) byTurn.set(turn, created)
    return created
  }

  for (const event of events) {
    switch (event.type) {
      case 'turn/start':
        turn = event.payload.turn
        break
      case 'user/message': {
        const { content, name, attachments } = event.payload
        if (!content && !attachments?.length) break
        if (name === CONTINUATION_NAME) {
          // The harness nudging a model that stopped mid-task; part of the process, not a message.
          agent(event.id).blocks.push({ kind: 'notice', id: event.id, tone: 'info', process: true, text: 'Reminded the agent to finish the task' })
          break
        }
        const guard = name ? LOOP_GUARD_NOTICES[name] : undefined
        if (guard) {
          agent(event.id).blocks.push({ kind: 'notice', id: event.id, tone: guard.tone, text: guard.text, ...(guard.process ? { process: true } : {}) })
          break
        }
        if (name === RECOVERY_NAME) {
          agent(event.id).blocks.push({ kind: 'notice', id: event.id, tone: 'info', text: 'Continued the run that stopped when the app closed' })
          break
        }
        const agentId = name?.match(AGENT_NAME)?.[1] ?? content.match(TERMINAL_REPLY)?.[1]
        if (agentId && addSubagentReply(entries, agentId, content)) break
        entries.push({ kind: 'user', id: event.id, text: content, ...(attachments?.length ? { images: attachments } : {}) })
        break
      }
      case 'assistant/message': {
        const target = agent(event.id)
        const { content, reasoning, toolCalls, interrupted } = event.payload
        if (reasoning?.trim()) {
          const durationMs = reasoningDuration(event.payload.stream)
          target.blocks.push({
            kind: 'reasoning', id: `reasoning-${event.id}`, text: reasoning.trim(),
            ...(durationMs !== undefined ? { durationMs } : {}),
          })
        }
        if (content) target.blocks.push({ kind: 'text', id: event.id, text: content })
        target.forkId = event.id
        for (const call of toolCalls ?? []) addToolCall(target, call.id, call.name, call.arguments)
        if (interrupted) target.status = 'stopped'
        if (turn !== undefined && !closed.has(turn)) {
          finalAnswers.delete(turn)
          if (content.trim() && !interrupted && !toolCalls?.length) {
            finalAnswers.set(turn, { text: content, sourceMessageId: event.id })
          }
        }
        break
      }
      case 'tool/call':
        addToolCall(agent(event.id), event.payload.id, event.payload.name, event.payload.arguments)
        break
      case 'tool/result': {
        const { toolCallId, name, ok, output, error, durationMs, attachments } = event.payload
        const target = agent(event.id)
        if (!findToolBlock(target, toolCallId)) addToolCall(target, toolCallId, name, undefined)
        settleTool(target, toolCallId, ok, output, error, durationMs, attachments)
        break
      }
      case 'checkpoint':
        if (event.payload.summary === undefined && event.payload.tokensBefore === undefined) break
        entries.push({
          kind: 'compaction',
          id: event.id,
          summary: event.payload.summary ?? '',
          ...(event.payload.tokensBefore !== undefined ? { tokensBefore: event.payload.tokensBefore } : {}),
        })
        break
      case 'meta': {
        if (event.payload.kind === 'approval/review' && event.payload.decision === 'allow') break
        if (event.payload.kind === 'ptc/dispatch-start' || event.payload.kind === 'ptc/dispatch') {
          addPtcDispatch(entries, event.payload)
          break
        }
        if (event.payload.kind === 'approval/review' && typeof event.payload.tool === 'string'
          && typeof event.payload.reason === 'string'
          && ['allow', 'deny', 'ask'].includes(String(event.payload.decision))) {
          agent(event.id).blocks.push({
            kind: 'notice', id: event.id, process: event.payload.decision === 'allow',
            tone: event.payload.decision === 'deny' ? 'error' : event.payload.decision === 'ask' ? 'warn' : 'info',
            text: approvalReviewNotice(event.payload),
          })
          break
        }
        const { kind, turn: summaryTurn, summary, sourceMessageId } = event.payload
        if (kind === 'run/summary' && typeof summaryTurn === 'number' && completed.has(summaryTurn)
          && typeof summary === 'string' && summary.trim() && typeof sourceMessageId === 'string') {
          const target = byTurn.get(summaryTurn)
          const source = finalAnswers.get(summaryTurn)
          if (target && source?.sourceMessageId === sourceMessageId) {
            target.summary = { text: summary, sourceMessageId }
          }
          break
        }
        const files = editedFiles(event.payload)
        if (files) {
          agent(event.id).blocks.push({ kind: 'files', id: event.id, files })
          break
        }
        const slash = slashMeta(event.payload)
        if (slash) entries.push({ kind: 'slash', id: event.id, ...slash })
        break
      }
      case 'llm/retry': {
        const { retry, delayMs, failure } = event.payload
        agent(event.id).blocks.push({
          kind: 'notice',
          id: event.id,
          tone: 'warn',
          text: `Retrying model request (attempt ${retry})${delayMs ? ` in ${formatDuration(delayMs)}` : ''}${failure ? ` — ${failure.message}` : ''}`,
        })
        break
      }
      case 'turn/end': {
        const { interrupted, cancelCause, error, finishReason, reason } = event.payload
        closed.add(event.payload.turn)
        const target = byTurn.get(event.payload.turn) ?? agent(event.id)
        const successful = !interrupted && !cancelCause && !error && target.status === 'done'
          && (reason ? reason.kind === 'completed' : finishReason === 'stop')
        if (successful) {
          completed.add(event.payload.turn)
          const summary = finalAnswers.get(event.payload.turn)
          if (summary) target.summary = summary
        }
        if (!interrupted && !cancelCause && !error) break
        target.status = error ? 'error' : 'stopped'
        target.blocks.push({ kind: 'notice', id: event.id, tone: error ? 'error' : 'info', text: endText(cancelCause, error) })
        break
      }
      default:
        break
    }
  }
  return entries
}

/** Keeps final answer, file summaries and failures outside the process disclosure. */
export function presentRun(entry: AgentEntry): { process: Block[]; visible: Block[] } {
  const summary = entry.summary
  if (!summary || entry.status !== 'done'
    || !entry.blocks.some(block => block.kind === 'text' && block.id === summary.sourceMessageId)) {
    return { process: [], visible: entry.blocks }
  }
  const process: Block[] = []
  const visible: Block[] = []
  for (const block of entry.blocks) {
    if (block.kind === 'text' && block.id === summary.sourceMessageId) {
      visible.push({ ...block, text: summary.text })
    } else if (block.kind === 'reasoning' && block.id === `reasoning-${summary.sourceMessageId}`) {
      // The thought behind the answer stays beside it, folded to one line.
      visible.push(block)
    } else if (block.kind === 'files' || (block.kind === 'notice' && !block.process)
      || (block.kind === 'tool' && (block.tool.status === 'running' || needsAttention(block.tool)))
      || (block.kind === 'subagent' && block.agent.status !== 'ready')) {
      visible.push(block)
    } else {
      process.push(block)
    }
  }
  return { process, visible }
}

/**
 * A running turn reads like the conversation it is: the agent's narration
 * stays in view as it is written, while each run of tool calls sits behind
 * one line that names the current step. The narration folds away only once
 * the turn ends (see `presentRun` / `presentOutcome`), leaving the answer.
 */
export function presentLive(entry: AgentEntry): { process: Block[]; visible: Block[] } {
  return { process: [], visible: entry.blocks.filter(block => !(block.kind === 'notice' && block.process)) }
}

/**
 * Outcome first: a finished turn shows its answer, the files it changed and
 * anything that went wrong; every other step folds away until asked for.
 * While the turn runs nothing streams into view — the thread's checklist
 * says what it is doing.
 */
export function presentOutcome(entry: AgentEntry, live: boolean): { process: Block[]; visible: Block[] } {
  let answer: Block | undefined
  let thought: Block | undefined
  if (!live) {
    for (let i = entry.blocks.length - 1; i >= 0 && !answer; i -= 1) {
      const block = entry.blocks[i]!
      if (block.kind === 'text' && block.text.trim()) {
        answer = block
        if (entry.blocks[i - 1]?.kind === 'reasoning') thought = entry.blocks[i - 1]
      }
    }
  }
  const process: Block[] = []
  const visible: Block[] = []
  for (const block of entry.blocks) {
    const shown = block === answer
      || block === thought
      || block.kind === 'files'
      || (block.kind === 'notice' && !block.process && block.tone !== 'info')
      || (!live && block.kind === 'tool' && needsAttention(block.tool))
    if (shown) visible.push(block)
    else process.push(block)
  }
  return { process, visible }
}

function addToolCall(target: AgentEntry, callId: string, name: string, args: unknown): void {
  if (findToolBlock(target, callId) || findSubagentBlock(target, callId)) return
  if (name === SPAWN_TOOL) {
    target.blocks.push({ kind: 'subagent', id: `subagent-${callId}`, agent: subagentFromArgs(callId, args) })
    return
  }
  target.blocks.push({ kind: 'tool', id: `tool-${callId}`, tool: { callId, name, args, status: 'running' } })
}

function settleTool(
  target: AgentEntry,
  callId: string,
  ok: boolean,
  output: unknown,
  error: ErrorInfo | undefined,
  durationMs: number | undefined,
  images?: ImageAttachment[],
): void {
  const sub = findSubagentBlock(target, callId)
  if (sub) {
    const id = typeof output === 'string' ? output.match(STARTED)?.[1] : undefined
    if (id) sub.agent.id = id
    if (sub.agent.status === 'starting') sub.agent.status = ok ? 'running' : 'failed'
    if (error) sub.agent.error = error.message
    return
  }
  const block = findToolBlock(target, callId)
  if (!block) return
  block.tool = {
    ...block.tool,
    status: ok ? 'ok' : 'error',
    ...(output !== undefined ? { output } : {}),
    ...(images?.length ? { images } : {}),
    ...(error ? { error: error.message } : {}),
    ...(error?.name ? { errorName: error.name } : {}),
    ...(durationMs !== undefined ? { durationMs } : {}),
  }
}

function approvalReviewNotice(payload: Record<string, unknown>): string {
  const heading = payload.decision === 'allow' ? '自动审批已通过'
    : payload.decision === 'deny' ? '自动审批已拒绝' : '需要你确认'
  const reason = typeof payload.reason === 'string' ? payload.reason : ''
  let explanation = reason
  if (/conflict probability .* exceeds/i.test(reason)) {
    explanation = '操作可能与你的要求或约束冲突，需要你确认。'
  } else if (/risk confidence .* below/i.test(reason)) {
    const risk = payload.risk === 'low' ? '低风险' : payload.risk === 'medium' ? '中等风险' : payload.risk === 'high' ? '高风险' : undefined
    explanation = risk ? `初步判断为${risk}，但还不够确定，需要你确认。` : '风险判断还不够确定，需要你确认。'
  } else if (/cancelled|timed out/i.test(reason)) {
    explanation = /approval mode changed/i.test(reason) ? '自动审批已取消或模式已改变，需要你确认。' : '自动审批已取消或超时，需要你确认。'
  } else if (/unavailable|invalid decision/i.test(reason)) {
    explanation = '自动审批暂不可用，需要你确认。'
  } else if (/no human task is available/i.test(reason)) {
    explanation = '缺少你的任务指示，需要你确认。'
  } else if (reason === 'Jev confidently classified the action as high risk.') {
    explanation = '操作被判定为高风险，已拒绝执行。'
  } else if (reason === 'Action meets risk confidence and conflict thresholds.') {
    explanation = '操作符合当前要求与审批条件。'
  }
  return `${heading}：${payload.tool} — ${explanation}`
}

function addPtcDispatch(entries: Entry[], payload: Record<string, unknown>): void {
  const { parentCallId, callId, name, kind } = payload
  if (typeof parentCallId !== 'string' || typeof callId !== 'string' || typeof name !== 'string') return
  for (let index = entries.length - 1; index >= 0; index -= 1) {
    const entry = entries[index]
    if (entry?.kind !== 'agent') continue
    const parent = findToolBlock(entry, parentCallId)
    if (!parent) continue
    const children = parent.tool.children ??= []
    let child = children.find(tool => tool.callId === callId)
    if (!child) {
      child = { callId, name, args: payload.input, status: 'running' }
      children.push(child)
    }
    if (kind === 'ptc/dispatch') {
      child.status = payload.ok === true ? 'ok' : 'error'
      const result = payload.result
      if (result && typeof result === 'object') {
        if ('output' in result) child.output = result.output
        if ('durationMs' in result && typeof result.durationMs === 'number') child.durationMs = result.durationMs
        if ('error' in result && result.error && typeof result.error === 'object'
          && 'message' in result.error && typeof result.error.message === 'string') {
          child.error = result.error.message
          if ('name' in result.error && typeof result.error.name === 'string') child.errorName = result.error.name
        }
      } else if (result !== undefined) child.output = result
    }
    return
  }
}

function findToolBlock(target: AgentEntry, callId: string) {
  return target.blocks.find((b): b is Extract<Block, { kind: 'tool' }> => b.kind === 'tool' && b.tool.callId === callId)
}

function findSubagentBlock(target: AgentEntry, callId: string) {
  return target.blocks.find((b): b is Extract<Block, { kind: 'subagent' }> => b.kind === 'subagent' && b.agent.callId === callId)
}

function addSubagentReply(entries: Entry[], agentId: string, content: string): boolean {
  const terminal = content.match(TERMINAL_REPLY)
  const text = terminal ? content.slice(terminal[0].length) : content
  for (let i = entries.length - 1; i >= 0; i -= 1) {
    const entry = entries[i]
    if (entry?.kind !== 'agent') continue
    const block = entry.blocks.find((b): b is Extract<Block, { kind: 'subagent' }> => b.kind === 'subagent' && b.agent.id === agentId)
    if (!block) continue
    if (text && block.agent.replies.at(-1) !== text) block.agent.replies.push(text)
    if (terminal) block.agent.status = terminal[2] === 'completed' ? 'ready' : 'failed'
    return true
  }
  return false
}

export function subagentFromArgs(callId: string, args: unknown): SubagentView {
  const record = args && typeof args === 'object' ? args as Record<string, unknown> : {}
  const task = typeof record.task === 'string' ? record.task : ''
  const label = typeof record.label === 'string' && record.label ? record.label : task.slice(0, 64) || 'Subagent'
  return { callId, label, task, mode: record.mode === 'fork' ? 'fork' : 'spawn', status: 'starting', replies: [] }
}

function editedFiles(payload: Record<string, unknown>): EditedFile[] | undefined {
  if (payload.kind !== 'files/edited' || !Array.isArray(payload.files)) return undefined
  const files = payload.files.flatMap((value: unknown): EditedFile[] => {
    if (typeof value === 'string') return [{ path: value }]
    if (!value || typeof value !== 'object') return []
    const { path, additions, deletions } = value as Record<string, unknown>
    if (typeof path !== 'string') return []
    return [{
      path,
      ...(typeof additions === 'number' ? { additions } : {}),
      ...(typeof deletions === 'number' ? { deletions } : {}),
    }]
  })
  return files.length ? files : undefined
}

function slashMeta(payload: Record<string, unknown>): { command: string; args: string[]; result: SlashResult } | undefined {
  if (payload.kind !== 'slash' || typeof payload.command !== 'string') return undefined
  const result = payload.result as SlashResult | undefined
  if (!result || (result.kind !== 'text' && result.kind !== 'json')) return undefined
  const args = Array.isArray(payload.args) ? payload.args.filter((a): a is string => typeof a === 'string') : []
  return { command: payload.command, args, result }
}

function endText(cause: CancelCause | undefined, error: ErrorInfo | undefined): string {
  if (error) return error.message
  if (!cause) return 'Interrupted'
  switch (cause.type) {
    case 'user': return 'Stopped by you'
    case 'parent': return 'Stopped by parent agent'
    case 'disposed': return 'Agent was shut down'
    case 'abort': return cause.message ? `Aborted: ${cause.message}` : 'Aborted'
    case 'timeout': return `Timed out after ${formatDuration(cause.timeoutMs)}`
  }
}

// ---------------------------------------------------------------------------
// Live Stream Events → Timeline
// ---------------------------------------------------------------------------

/** Append the optimistic user message and an empty running agent turn. */
export function beginRun(entries: readonly Entry[], prompt: string, now = Date.now(), images: readonly ImageAttachment[] = []): Entry[] {
  return [
    ...entries,
    { kind: 'user', id: `local-user-${now}`, text: prompt, ...(images.length ? { images: [...images] } : {}), local: true },
    { kind: 'agent', id: `local-agent-${now}`, blocks: [], status: 'running' },
  ]
}

/** Advance the running turn with one Stream Event. Returns the same array when nothing changed. */
export function applyStream(entries: readonly Entry[], event: StreamEvent): readonly Entry[] {
  const last = entries.at(-1)
  if (event.type === 'session/compaction') {
    if (entries.some(entry => entry.kind === 'compaction' && entry.id === event.id)) return entries
    const marker: Entry = {
      kind: 'compaction', id: event.id, summary: event.summary,
      ...(event.tokensBefore !== undefined ? { tokensBefore: event.tokensBefore } : {}),
    }
    // Keep the current Agent Run last so subsequent stream frames still reach it.
    return last?.kind === 'agent' && last.status === 'running'
      ? [...entries.slice(0, -1), marker, last]
      : [...entries, marker]
  }
  if (last?.kind !== 'agent') return entries
  const next = applyToAgent(last, event)
  return next === last ? entries : [...entries.slice(0, -1), next]
}

function applyToAgent(entry: AgentEntry, event: StreamEvent): AgentEntry {
  const blocks = entry.blocks
  switch (event.type) {
    case 'ptc/dispatch': {
      const next: AgentEntry = { ...entry, blocks: blocks.map(block => block.kind === 'tool'
        ? { ...block, tool: { ...block.tool, ...(block.tool.children ? { children: block.tool.children.map(child => ({ ...child })) } : {}) } } : block) }
      addPtcDispatch([next], event.payload)
      return next
    }
    case 'message_start':
      return { ...entry, blocks: [...blocks, { kind: 'text', id: `live-${event.id}`, text: '', streaming: true }] }
    case 'reasoning_delta': {
      const index = findLastIndex(blocks, b => b.kind === 'reasoning' && b.id === `live-reasoning-${event.id}`)
      if (index >= 0) {
        const block = blocks[index] as Extract<Block, { kind: 'reasoning' }>
        return { ...entry, blocks: replaceAt(blocks, index, { ...block, text: block.text + event.delta }) }
      }
      // Reasoning comes before the answer it leads to, ahead of that message's text block.
      const created: Block = { kind: 'reasoning', id: `live-reasoning-${event.id}`, text: event.delta, streaming: true, startedAt: Date.now() }
      const text = findLastIndex(blocks, b => b.kind === 'text' && b.id === `live-${event.id}`)
      const at = text < 0 ? blocks.length : text
      return { ...entry, blocks: [...blocks.slice(0, at), created, ...blocks.slice(at)] }
    }
    case 'message_delta': {
      const settled = settleReasoning(blocks, event.id)
      const index = findLastIndex(settled, b => b.kind === 'text' && b.id === `live-${event.id}`)
      if (index < 0) {
        return { ...entry, blocks: [...settled, { kind: 'text', id: `live-${event.id}`, text: event.delta, streaming: true }] }
      }
      const block = settled[index] as Extract<Block, { kind: 'text' }>
      return { ...entry, blocks: replaceAt(settled, index, { ...block, text: block.text + event.delta }) }
    }
    case 'message_stop': {
      const id = `live-${event.id}`
      return {
        ...entry,
        blocks: settleReasoning(blocks, event.id).flatMap(b => {
          if (b.kind === 'reasoning' && b.id === `live-reasoning-${event.id}`) return b.text.trim() ? [b] : []
          if (b.kind !== 'text' || b.id !== id) return [b]
          return b.text.trim() ? [{ ...b, streaming: false }] : []
        }),
      }
    }
    case 'tool/start': {
      const { id, name, arguments: args } = event.call
      if (blocks.some(b => (b.kind === 'tool' && b.tool.callId === id) || (b.kind === 'subagent' && b.agent.callId === id))) return entry
      const block: Block = name === SPAWN_TOOL
        ? { kind: 'subagent', id: `subagent-${id}`, agent: subagentFromArgs(id, args) }
        : { kind: 'tool', id: `tool-${id}`, tool: { callId: id, name, args, status: 'running' } }
      return { ...entry, blocks: [...blocks, block] }
    }
    case 'tool/end': {
      const { call, result } = event
      return {
        ...entry,
        blocks: blocks.map((b): Block => {
          if (b.kind === 'subagent' && b.agent.callId === call.id) {
            const id = typeof result.output === 'string' ? result.output.match(STARTED)?.[1] : undefined
            return {
              ...b,
              agent: {
                ...b.agent,
                ...(id ? { id } : {}),
                status: result.ok ? 'running' : 'failed',
                ...(result.error ? { error: result.error.message } : {}),
              },
            }
          }
          if (b.kind !== 'tool' || b.tool.callId !== call.id) return b
          return {
            ...b,
            tool: {
              ...b.tool,
              status: result.ok ? 'ok' : 'error',
              ...(result.output !== undefined ? { output: result.output } : {}),
              ...(result.attachments?.length ? { images: result.attachments } : {}),
              ...(result.error ? { error: result.error.message } : {}),
              ...(result.error?.name ? { errorName: result.error.name } : {}),
              ...(result.durationMs !== undefined ? { durationMs: result.durationMs } : {}),
            },
          }
        }),
      }
    }
    case 'plan/error':
      return { ...entry, blocks: [...blocks, { kind: 'notice', id: `plan-error-${blocks.length}`, tone: 'error', text: `Planning failed: ${event.message}` }] }
    case 'error':
      return {
        ...entry,
        status: 'error',
        blocks: [...blocks, { kind: 'notice', id: `error-${blocks.length}`, tone: 'error', text: event.message }],
      }
    case 'done':
      return { ...entry, status: entry.status === 'running' ? 'done' : entry.status }
    default:
      return entry
  }
}

/** Close a message's live reasoning once its answer (or the message) begins to settle. */
function settleReasoning(blocks: readonly Block[], messageId: string): readonly Block[] {
  const index = findLastIndex(blocks, b => b.kind === 'reasoning' && b.id === `live-reasoning-${messageId}`)
  const block = blocks[index]
  if (block?.kind !== 'reasoning' || !block.streaming) return blocks
  const { startedAt, ...rest } = block
  return replaceAt(blocks, index, {
    ...rest,
    streaming: false,
    ...(startedAt !== undefined ? { durationMs: Math.max(0, Date.now() - startedAt) } : {}),
  })
}

/** How long the model reasoned: from its first reasoning chunk to the first chunk of anything else. */
function reasoningDuration(stream: ReadonlyArray<{ time: number; chunk: { type: string } }> | undefined): number | undefined {
  let start: number | undefined
  let end: number | undefined
  for (const record of stream ?? []) {
    if (record.chunk.type === 'reasoning_delta') {
      start ??= record.time
      end = record.time
    } else if (start !== undefined && record.chunk.type !== 'message_start') {
      end = record.time
      break
    }
  }
  return start !== undefined && end !== undefined ? end - start : undefined
}

function findLastIndex<T>(items: readonly T[], predicate: (item: T) => boolean): number {
  for (let i = items.length - 1; i >= 0; i -= 1) if (predicate(items[i]!)) return i
  return -1
}

function replaceAt<T>(items: readonly T[], index: number, value: T): T[] {
  const copy = items.slice()
  copy[index] = value
  return copy
}

// ---------------------------------------------------------------------------
// Presentation helpers
// ---------------------------------------------------------------------------

export function formatDuration(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`
  const seconds = ms / 1000
  if (seconds < 60) return `${seconds < 10 ? seconds.toFixed(1) : Math.round(seconds)}s`
  const minutes = Math.floor(seconds / 60)
  return `${minutes}m ${Math.round(seconds % 60)}s`
}

export function formatTokens(n: number): string {
  if (n < 1000) return String(n)
  if (n < 1_000_000) return `${(n / 1000).toFixed(n < 10_000 ? 1 : 0)}k`
  return `${(n / 1_000_000).toFixed(1)}M`
}

export function stringify(value: unknown): string {
  if (value === undefined) return ''
  if (typeof value === 'string') return value
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}
